import { BookOpen, Bookmark } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import type { StepCitation } from "@/lib/api";

/**
 * The step reading card, resolved from the user's own literature (Fellowships
 * PRD §10.2–3). Three states, and all three are intentional:
 *
 *   - Nothing selected → "Your own reading", in the SAME card geometry as a
 *     citation, so it reads as a reading assignment rather than a missing one.
 *     People work the steps outside any fellowship, with a therapist, or from a
 *     book of their own. The app does not guess, and never falls back to AA.
 *   - One selection → that fellowship's citation, unlabelled.
 *   - Two or more → each citation labelled by fellowship, in catalogue order,
 *     with a line saying the app has no preference. No primary is inferred.
 *
 * Citations only, always: a title and a page or chapter reference, never a line
 * of anyone's literature (§8). Unverified sets stay chapter-level on purpose —
 * no page number is invented while the owner is still checking editions (§6).
 */
export function StepReadings({
  citations,
  className,
}: {
  citations: StepCitation[];
  className?: string;
}) {
  if (citations.length === 0) {
    return (
      <div className={className}>
        <Card>
          <CardContent className="flex items-start gap-3 p-4">
            <BookOpen className="mt-0.5 h-5 w-5 shrink-0 text-primary" strokeWidth={2} />
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                Reading
              </p>
              <p className="mt-0.5 text-sm font-semibold text-foreground">Your own reading</p>
              <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                Whatever you and your sponsor are reading on this step. The app doesn't need to
                know what it is.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const multiple = citations.length > 1;
  return (
    <div className={`flex flex-col gap-3 ${className ?? ""}`}>
      {citations.map((c) => (
        <div key={c.fellowship} className="flex flex-col gap-3">
          <Card>
            <CardContent className="flex items-start gap-3 p-4">
              <BookOpen className="mt-0.5 h-5 w-5 shrink-0 text-primary" strokeWidth={2} />
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                  {multiple ? `Reading · ${c.short}` : "Reading"}
                </p>
                <p className="mt-0.5 text-sm font-semibold text-foreground">{c.citation}</p>
                {/* Which edition the range refers to, so nobody has to guess
                    whether their copy paginates the same way. */}
                {c.edition && (
                  <p className="mt-0.5 text-xs text-muted-foreground">{c.edition}</p>
                )}
                <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                  {c.citation_note ??
                    "Read it in your own copy. No part of the text is reproduced here."}
                </p>
              </div>
            </CardContent>
          </Card>
          {c.also_citation && (
            <Card>
              <CardContent className="flex items-start gap-3 p-4">
                <Bookmark className="mt-0.5 h-5 w-5 shrink-0 text-primary" strokeWidth={2} />
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                    Also referenced{multiple ? ` · ${c.short}` : ""}
                  </p>
                  <p className="mt-0.5 text-sm font-semibold text-foreground">{c.also_citation}</p>
                  {c.also_note && (
                    <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                      {c.also_note}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      ))}
      {multiple && (
        <p className="text-[12.5px] leading-relaxed text-muted-foreground">
          Both are shown because you use both. Read either, or both — the app has no preference.
        </p>
      )}
    </div>
  );
}

/**
 * The one-line explanation under an empty reading card. Separate from the card
 * so a screen can place it where it fits, and so the card itself stays quiet.
 */
export function NoLiteratureNote() {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border bg-secondary px-4 py-3">
      <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={2} />
      <p className="text-[12.5px] leading-relaxed text-muted-foreground">
        You haven't told the app which literature you use, so it isn't guessing. If you'd like the
        readings cited, you can pick your literature in Settings — or leave it as it is.
      </p>
    </div>
  );
}
